import { useEffect, useState } from "react";

import { Box, Stack, Typography } from "@mui/material";

import { useAtom, useAtomValue } from "jotai";

import { isUnityViewerFullScreenAtom } from "store/atoms/isUnityViewerFullScreen";
import { isInstructionSplashScreenOpenAtom } from "store/atoms/virtualTourViewer/isInstructionSplashScreenOpen";

import annotationIcon from "../../../../assets/images/icons/vtSplash/anotation.png";
import measurementIcon from "../../../../assets/images/icons/vtSplash/measurement.png";
import viewIcon from "../../../../assets/images/icons/vtSplash/view.png";

import ViewControlCard from "./components/viewControlsCard";
import { styles } from "./style";
import useCurrentUserProfile from "hooks/useCurrentUserProfile";
import { useTurnOffVtourManualOnStartMutation } from "api/userProfiles/useTurnOffVtourManualOnStartMutation";
import { Icon } from "@iconify/react";
import AnnotationsPage from "./components/AnnotationsPage";
import MeasurementsPage from "./components/MeasurementsPage";

type ManualSection = {
  id: string;
  label: string;
  content: React.ReactNode;
  icon: string;
};

const manualSections: ManualSection[] = [
  {
    id: 'views-and-controls',
    label: 'Views and Controls',
    content: (
      <ViewControlCard />
    ),
    icon: viewIcon
  },
  {
    id: 'annotations',
    label: 'Annotations',
    content: (
      <AnnotationsPage />
    ),
    icon: annotationIcon
  },
  {
    id: 'measurements',
    label: 'Measurements',
    content: (
      <MeasurementsPage />
    ),
    icon: measurementIcon
  }
] as const;

const Manual = () => {
  const fullscreen = useAtomValue(isUnityViewerFullScreenAtom);
  const [isInstructionSplashScreenOpen, setIsInstructionSplashScreenOpen] =
    useAtom(isInstructionSplashScreenOpenAtom);
  const [currentManualSection, setCurrentManualSection] = useState<ManualSection>(manualSections[0]);
  const [shouldInstructionSplashScreen, setShouldInstructionSplashScreen] = useState(true);

  const { userProfile } = useCurrentUserProfile();

  useEffect(() => {
    setShouldInstructionSplashScreen(userProfile?.metadata?.showVirtualTourManual ?? true);
  }, [userProfile]);

  const show = isInstructionSplashScreenOpen || shouldInstructionSplashScreen;

  const { turnOffVtourManualOnStart } = useTurnOffVtourManualOnStartMutation();

  return (
    <Stack
      sx={{
        ...styles.manualContainer,
        ...{
          opacity:
            show
              ? 1
              : 0,
          transform:
            show
              ? "opacity(1)"
              : "opacity(0)",
          transition: "all 0.3s ease",
          pointerEvents:
            show
              ? "auto"
              : "none",
          userSelect: "none",
        },
      }}
    >
      <Icon
        icon="ri:close-line"
        style={{
          position: "absolute",
          top: "2rem",
          fontSize: "2rem",
          right: "2rem",
          cursor: "pointer",
        }}
        onClick={() => {
          setIsInstructionSplashScreenOpen(false);
          setShouldInstructionSplashScreen(false);
          if (shouldInstructionSplashScreen) {
            turnOffVtourManualOnStart();
          }
        }}
      />
      <Stack
        sx={{
          padding: 2,
          height: "100%",
          width: "100%",
          gap: 2,
        }}
      >
        <Stack
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            gap: 2,
          }}
        >
          {Object.values(manualSections).map(({ id, label, icon }) => (
            <Stack
              sx={{
                ...styles.manualHeaderText,
                backgroundColor:
                  id === currentManualSection.id ? "grey.800" : "background.paper",
                paddingY: fullscreen ? 2 : 1,
              }}
              key={id}
              onClick={() => {
                const chosenManualSection = manualSections.find(section => section.id === id);
                if (!chosenManualSection) return;
                setCurrentManualSection(chosenManualSection);
              }}
            >
              <Box
                component={"img"}
                src={icon}
                alt={label}
                sx={{
                  ...styles.manualHeaderIcon,
                  width: "2rem",
                }}
              />
              <Typography variant="caption" sx={{
                textWrap: "balance"
              }}>{label}</Typography>
            </Stack>
          ))}
        </Stack>
        <Stack
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            height: {
              md: "100%",
            },
          }}
        >
          {currentManualSection.content}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default Manual;
